import React, { useMemo } from "react";
import cx from "classnames";
import dayjs from "dayjs";
import Nodata from "assets/images/home/nodata.png";
import NotificationPublicFeed from "@/components/Pages/Notification/NotificationPublicFeed";
import {
  isPublicFeedTab,
  PUBLIC_FEED_TAB,
} from "@/components/Pages/Notification/utils/publicFeedConstants";
import { hasUserLetterClickAction, isHyperpayCardDepositLetter } from "@/utils/userLetterJump";
import styles from "./index.module.less";

function IconChevronRight({ className }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconInbox({ className }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <g clipPath="url(#notification_pc_inbox_clip)">
        <rect x="1" y="7" width="18" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M2.12083 17.8009L3.5835 10H16.521L18.4565 17.7419C18.6162 18.381 18.1329 19 17.4742 19H3.11598C2.48202 19 2.00399 18.424 2.12083 17.8009Z"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path d="M11.5 7V2.5C11.5 1.67157 10.8284 1 10 1V1C9.17157 1 8.5 1.67157 8.5 2.5V7" stroke="currentColor" strokeWidth="1.8" />
      </g>
      <defs>
        <clipPath id="notification_pc_inbox_clip">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function IconSettings({ className }) {
  return (
    <svg className={className} width="19" height="20" viewBox="0 0 19 20" fill="none" aria-hidden>
      <path
        d="M17.1967 4.26772L11.5553 1.07529C11.0786 0.807437 10.5409 0.666748 9.99414 0.666748C9.44733 0.666748 8.90972 0.807437 8.43301 1.07529L2.79295 4.26772C2.32165 4.53205 1.92878 4.91652 1.65433 5.38201C1.37987 5.84749 1.23363 6.37737 1.23047 6.91773V13.2999C1.23047 14.3915 1.82956 15.4048 2.79025 15.9499L8.43301 19.1396C8.90951 19.4081 9.4472 19.5492 9.99414 19.5492C10.5411 19.5492 11.0788 19.4081 11.5553 19.1396L17.1953 15.9499C17.6664 15.6854 18.059 15.3009 18.3332 14.8354C18.6074 14.3699 18.7534 13.8401 18.7565 13.2999V6.91773C18.7533 6.37736 18.6072 5.84746 18.333 5.3818C18.0588 4.91614 17.6663 4.53131 17.1953 4.26637L17.1967 4.26772ZM17.4261 13.2999C17.4247 13.6102 17.341 13.9145 17.1836 14.1819C17.0262 14.4493 16.8007 14.6701 16.5301 14.8219L10.8901 18.0116C10.3369 18.322 9.65547 18.322 9.10091 18.0116L3.45815 14.8219C3.18737 14.6703 2.96172 14.4495 2.80429 14.1821C2.64687 13.9147 2.56333 13.6102 2.56222 13.2999V6.91773C2.56222 6.29436 2.90494 5.71011 3.45815 5.39843L9.09956 2.20734C9.37275 2.05317 9.68112 1.97216 9.99481 1.97216C10.3085 1.97216 10.6169 2.05317 10.8901 2.20734L16.5301 5.39843C17.0847 5.71011 17.4274 6.29301 17.4274 6.91908V13.2999H17.4261Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <path
        d="M10.0088 6.57386C8.01587 6.57521 6.40076 8.15793 6.39941 10.1104C6.39941 12.0641 8.01587 13.6482 10.0088 13.6496C12.003 13.6496 13.6195 12.0641 13.6208 10.1104C13.6195 8.15658 12.0017 6.57251 10.0088 6.57251V6.57386ZM10.0088 12.3461C8.75124 12.3448 7.73252 11.345 7.72847 10.1104C7.72847 8.87846 8.75393 7.87593 10.0088 7.87593C11.2677 7.87593 12.2891 8.87846 12.2891 10.1104C12.2891 11.3436 11.2677 12.3448 10.0088 12.3448V12.3461Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </svg>
  );
}

const BLURB_KEYS = {
  [PUBLIC_FEED_TAB.ANNOUNCEMENT]: "notification.pcBlurbPublicAnnouncement",
  [PUBLIC_FEED_TAB.NEWS]: "notification.pcBlurbPublicNews",
  [PUBLIC_FEED_TAB.ACADEMY]: "notification.pcBlurbPublicAcademy",
  4: "notification.pcBlurbAnnouncement",
  3: "notification.pcBlurbActivity",
  2: "notification.pcBlurbLaunch",
  1: "notification.pcBlurbSystem",
};

const BLURB_FALLBACKS = {
  [PUBLIC_FEED_TAB.ANNOUNCEMENT]: "Official announcements and platform updates.",
  [PUBLIC_FEED_TAB.NEWS]: "Latest market news and industry updates.",
  [PUBLIC_FEED_TAB.ACADEMY]: "Guides and tutorials from Spikex Academy.",
  4: "Official announcements and important platform updates.",
  3: "Promotions, events and limited-time campaigns.",
  2: "New assets and trading pairs on SpikeX.",
  1: "System notices, maintenance and service updates.",
};

export default function NotificationPc({
  t,
  navData,
  navActive,
  tabReadMap,
  onNavClick,
  notificationData,
  onReadAll,
  onGoSetting,
  onOpenUrl,
  onMarkRead,
  isMore,
  onViewMore,
  publicFeedState,
  publicFeedLoading,
  publicFeedMoreUrl,
}) {
  const activeNav = useMemo(
    () => navData.find((item) => item.type === navActive) || navData[0],
    [navData, navActive]
  );

  const isPublicFeed = isPublicFeedTab(navActive);

  const blurbKey = BLURB_KEYS[navActive];
  const blurbFallback = BLURB_FALLBACKS[navActive] || "";
  const blurb = (() => {
    if (!blurbKey) return blurbFallback;
    const v = t(blurbKey);
    return v && v !== blurbKey ? v : blurbFallback;
  })();

  const pageTitle = (() => {
    const v = t("notification.pcTitle");
    return v && v !== "notification.pcTitle" ? v : "Announcement Center";
  })();

  return (
    <div className={cx(styles.shell, styles.spikexPc)}>
      <div className={styles.inner}>
        <div className={styles.pageHead}>
          <h1 className={styles.pageTitle}>{pageTitle}</h1>
          <div className={styles.pageActions}>
            {!isPublicFeed ? (
              <button type="button" className={styles.actionBtn} onClick={onReadAll}>
                <IconInbox className={styles.actionIcon} />
                {t("user.readAll")}
              </button>
            ) : null}
            <button type="button" className={styles.actionBtn} onClick={onGoSetting}>
              <IconSettings className={styles.actionIcon} />
              {t("notice.setting")}
            </button>
          </div>
        </div>

        <div className={styles.grid}>
          <aside className={styles.rail}>
            <nav className={styles.railNav} aria-label={pageTitle}>
              {navData.map((item) => {
                const active = navActive === item.type;
                const unread = !item.isPublicFeed && !!item.num && !tabReadMap[String(item.type)];
                return (
                  <React.Fragment key={String(item.type)}>
                    {item.showDividerBefore ? <div className={styles.railDivider} aria-hidden /> : null}
                    <button
                      type="button"
                      className={cx(styles.railItem, active && styles.railItemActive)}
                      aria-current={active ? "true" : undefined}
                      onClick={() => onNavClick(item)}
                    >
                      <span className={styles.railIcon}>{item.icon}</span>
                      <span className={styles.railLabel}>{item.name}</span>
                      {unread ? <span className={styles.railBadge}>{item.num}</span> : null}
                    </button>
                  </React.Fragment>
                );
              })}
            </nav>
          </aside>

          <section className={styles.main}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>{activeNav?.name}</h2>
              {blurb ? <p className={styles.sectionDesc}>{blurb}</p> : null}
            </div>

            {isPublicFeed ? (
              <NotificationPublicFeed
                t={t}
                activeTab={navActive}
                feedState={publicFeedState}
                moreUrl={publicFeedMoreUrl}
                loading={publicFeedLoading}
              />
            ) : !notificationData.length ? (
              <div className={styles.emptyCard}>
                <img src={Nodata} alt="" className={styles.emptyImg} />
                <p>{t("home.noData")}</p>
              </div>
            ) : (
              <ul className={styles.listCard}>
                {notificationData.map((item) => {
                  const showDetails = hasUserLetterClickAction(item);
                  return (
                    <li key={item.id} className={styles.listItem}>
                      <div
                        className={cx(styles.row, item.isRead && styles.rowRead)}
                        onClick={() => {
                          if (isHyperpayCardDepositLetter(item)) {
                            onOpenUrl(item);
                            return;
                          }
                          onMarkRead(item);
                        }}
                        role="presentation"
                      >
                        <div className={styles.rowMain}>
                          <div className={styles.rowMeta}>
                            <span className={styles.rowTag}>{activeNav?.name}</span>
                            <span className={cx(styles.rowDate, styles.tnum)}>
                              {dayjs(item.createTime).format("YYYY-MM-DD")}
                            </span>
                            {!item.isRead ? <span className={styles.unreadDot} aria-hidden /> : null}
                          </div>
                          <h3 className={styles.rowTitle}>{item.title}</h3>
                          {item.content ? <p className={styles.rowExcerpt}>{item.content}</p> : null}
                          {showDetails ? (
                            <button
                              type="button"
                              className={styles.detailsBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenUrl(item);
                              }}
                            >
                              {t("noticition.ViewDetails")}
                            </button>
                          ) : null}
                        </div>
                        {item.hotActivityImageUrl ? (
                          <img src={item.hotActivityImageUrl} alt="" className={styles.rowCover} />
                        ) : null}
                        <IconChevronRight className={styles.rowChevron} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {!isPublicFeed && isMore ? (
              <button type="button" className={styles.loadMore} onClick={onViewMore}>
                {t("cmpt.viewmore")}
              </button>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
